import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import WaiverFormBuilder from './WaiverFormBuilder.jsx';
import './WaiverManagement.css';

const WaiverManagement = () => {
  const { user } = useAuth();
  const toast = useToast();
  const token = user?.token;
  const [view, setView] = useState('compliance'); // 'compliance', 'templates', 'submissions'
  const [templates, setTemplates] = useState([]);
  const [complianceList, setComplianceList] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [alerts, setAlerts] = useState({ pending: 0, expiring: 0 });
  
  // View submission modal
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchTemplates();
    fetchComplianceList();
    fetchAlerts();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/forms/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchComplianceList = async () => {
    try {
      // Get all athletes and their compliance (canonical roster used app-wide)
      const res = await fetch(`${API_BASE_URL}/api/v1/athletes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const athletes = await res.json();
        
        // Fetch compliance for each athlete
        const complianceData = await Promise.all(
          athletes.map(async (athlete) => {
            const compRes = await fetch(`${API_BASE_URL}/api/forms/athlete/${athlete.id}/compliance`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (compRes.ok) {
              const comp = await compRes.json();
              return { athlete, compliance: comp };
            }
            return null;
          })
        );
        
        setComplianceList(complianceData.filter(c => c !== null));
      }
    } catch (error) {
      console.error('Error fetching compliance:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/forms/compliance-alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAlerts({
          pending: data.filter(a => a.status === 'pending').length,
          expiring: data.filter(a => a.status === 'expiring_soon').length
        });
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  // Template creation/editing now lives in <WaiverFormBuilder /> (Templates tab).

  const viewSubmission = async (submissionId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/forms/submissions/${submissionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedSubmission(data);
        setShowSubmissionModal(true);
      }
    } catch (error) {
      console.error('Error fetching submission:', error);
    }
  };

  const getAge = (dob) => {
    if (!dob) return 'N/A';
    const birth = new Date(dob);
    if (isNaN(birth)) return 'N/A';
    const diff = Date.now() - birth.getTime();
    return Math.abs(new Date(diff).getUTCFullYear() - 1970);
  };

  // Nudge an athlete's parents about forms they haven't signed (or that lapsed).
  // Works for never-signed athletes — the backend creates the compliance record.
  const handleSendReminder = async (athlete, pending) => {
    try {
      for (const c of pending) {
        const res = await fetch(`${API_BASE_URL}/api/waivers/remind-athlete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ athlete_id: athlete.id, form_template_id: c.form_template_id })
        });
        const j = await res.json();
        if (!res.ok) throw new Error(j.error || 'reminder failed');
      }
      toast.success(`Reminder sent to ${athlete.name}'s family (${pending.length} form${pending.length !== 1 ? 's' : ''}).`);
    } catch (err) {
      toast.error('Could not send reminder: ' + err.message);
    }
  };

  const getComplianceStatus = (compliance) => {
    if (!compliance || compliance.length === 0) return 'missing';
    
    const hasExpired = compliance.some(c => c.status === 'expired');
    const hasExpiring = compliance.some(c => c.status === 'expiring_soon');
    const allCompliant = compliance.every(c => c.status === 'compliant');
    
    if (hasExpired) return 'expired';
    if (hasExpiring) return 'expiring';
    if (allCompliant) return 'compliant';
    return 'pending';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant': return '#4CAF50';
      case 'expiring': return '#FF9800';
      case 'expired': return '#f44336';
      case 'pending': return '#2196F3';
      default: return '#999';
    }
  };

  return (
    <div className="waiver-management">
      <div className="header">
        <h1>Waivers & Forms</h1>
      </div>

      {/* Alerts */}
      {(alerts.pending > 0 || alerts.expiring > 0) && (
        <div className="alerts-banner">
          {alerts.pending > 0 && (
            <div className="alert alert-warning">
              <strong>{alerts.pending}</strong> athlete{alerts.pending !== 1 ? 's' : ''} with pending waivers
            </div>
          )}
          {alerts.expiring > 0 && (
            <div className="alert alert-danger">
              <strong>{alerts.expiring}</strong> waiver{alerts.expiring !== 1 ? 's' : ''} expiring soon
            </div>
          )}
        </div>
      )}

      {/* View Tabs */}
      <div className="view-tabs">
        <button 
          className={view === 'compliance' ? 'active' : ''} 
          onClick={() => setView('compliance')}
        >
          Compliance Dashboard
        </button>
        <button 
          className={view === 'templates' ? 'active' : ''} 
          onClick={() => setView('templates')}
        >
          Templates ({templates.length})
        </button>
      </div>

      {/* Compliance Dashboard View */}
      {view === 'compliance' && (
        <div className="compliance-dashboard">
          <table>
            <thead>
              <tr>
                <th>Athlete</th>
                <th>Parent</th>
                <th>Status</th>
                <th>Waivers</th>
                <th>Next Expiration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {complianceList.map(({ athlete, compliance }) => {
                const status = getComplianceStatus(compliance);
                const nextExpiration = compliance.length > 0 
                  ? compliance.reduce((earliest, c) => 
                      !earliest || new Date(c.expires_at) < new Date(earliest) 
                        ? c.expires_at 
                        : earliest, null)
                  : null;
                
                return (
                  <tr key={athlete.id}>
                    <td>
                      <div className="athlete-info">
                        <strong>{athlete.name}</strong>
                        <span className="athlete-age">Age {getAge(athlete.dob)}</span>
                      </div>
                    </td>
                    <td>{athlete.parent_name || 'N/A'}</td>
                    <td>
                      <span 
                        className="status-badge" 
                        style={{ background: getStatusColor(status) }}
                      >
                        {status}
                      </span>
                    </td>
                    <td>
                      {compliance.length > 0 ? (
                        <div className="waiver-list">
                          {compliance.map((c, i) => (
                            <button
                              key={i}
                              className="waiver-link"
                              onClick={() => viewSubmission(c.submission_id)}
                            >
                              {c.form_name}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="no-waivers">None submitted</span>
                      )}
                    </td>
                    <td>
                      {nextExpiration ? (
                        <span className={
                          new Date(nextExpiration) < new Date(Date.now() + 30*24*60*60*1000)
                            ? 'expiration-warning'
                            : ''
                        }>
                          {new Date(nextExpiration).toLocaleDateString()}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {(() => {
                        const pending = (compliance || []).filter(c =>
                          ['missing', 'expired', 'expiring_soon'].includes(c.status));
                        return pending.length > 0 ? (
                          <button className="btn-small btn-secondary" onClick={() => handleSendReminder(athlete, pending)}>
                            📧 Send Reminder
                          </button>
                        ) : <span className="all-signed">✓ All signed</span>;
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {complianceList.length === 0 && (
            <div className="empty-state">
              No athletes found. Athletes will appear here once enrolled.
            </div>
          )}
        </div>
      )}

      {/* Templates View — unified builder (upload PDF or write/paste) */}
      {view === 'templates' && <WaiverFormBuilder />}

      {/* View Submission Modal */}
      {showSubmissionModal && selectedSubmission && (
        <div className="modal-overlay" onClick={() => setShowSubmissionModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Waiver Submission</h2>
              <button className="close-btn" onClick={() => setShowSubmissionModal(false)}>×</button>
            </div>

            <div className="submission-details">
              <div className="detail-row">
                <strong>Athlete:</strong>
                <span>{selectedSubmission.athlete_name}</span>
              </div>
              <div className="detail-row">
                <strong>Form:</strong>
                <span>{selectedSubmission.form_name}</span>
              </div>
              <div className="detail-row">
                <strong>Submitted:</strong>
                <span>{new Date(selectedSubmission.submitted_at).toLocaleString()}</span>
              </div>
              <div className="detail-row">
                <strong>Expires:</strong>
                <span>{new Date(selectedSubmission.expires_at).toLocaleDateString()}</span>
              </div>

              {selectedSubmission.signature_data && (
                <div className="signature-section">
                  <strong>Digital Signature:</strong>
                  <img src={selectedSubmission.signature_data} alt="Signature" />
                </div>
              )}

              <div className="form-data-section">
                <strong>Submitted Data:</strong>
                <pre>{JSON.stringify(selectedSubmission.form_data, null, 2)}</pre>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSubmissionModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaiverManagement;
